import { useCallback, useMemo } from 'react';

import { Chart, LegendTilesConfig } from '@dbt-labs/charts';
import { Link, Section } from '@dbt-labs/sourdough';

import { CostInsight, Grouping } from '../../typings/costInsights';
import {
  formatMinutes,
  getTimePeriodLabel,
  groupDataByTimeGrouping,
} from '../../util/costInsights';
import { TEAL_COLOR_WITH_OPACITY } from './const';
import { GroupingSelector } from './GroupingSelector';
import {
  createEmptyLegendTile,
  legendTilesBuilder,
  usePartialDataTooltipHeader,
} from './utils';

const PRIMARY_DATA_LABEL = 'Total time';
const SECONDARY_DATA_LABEL = 'Total time without optimization';
const AREA_DATA_LABEL = 'Total time reduction';

type RunChartProps = {
  data: CostInsight[];
  isLoading: boolean;
  isPreparing?: boolean;
  grouping: Grouping;
  setGrouping?: (grouping: Grouping) => void;
  infoTooltip?: React.ReactNode;
  statusBadge?: React.ReactNode;
  enableGroupingSelector?: boolean;
  selector?: React.ReactNode;
};

export const RunChart = ({
  data,
  isLoading,
  isPreparing = false,
  grouping,
  setGrouping,
  infoTooltip,
  statusBadge,
  enableGroupingSelector = true,
  selector,
}: RunChartProps) => {
  const groupedData = useMemo(
    () => (data ? groupDataByTimeGrouping(data, grouping) : []),
    [data, grouping],
  );

  // Convert from seconds to minutes for display
  // Primary data: actual execution time
  // Secondary data: execution time without savings (actual + saved)
  const chartData = useMemo<{
    totalTimeData: Array<{ date: Date; value: number }>;
    totalTimeWithoutSavingsData: Array<{ date: Date; value: number }>;
    totalTimeSavingsData: Array<{ date: Date; value: number }>;
  }>(() => {
    const totalTimeData: Array<{ date: Date; value: number }> = [];
    const totalTimeWithoutSavingsData: Array<{ date: Date; value: number }> = [];
    const totalTimeSavingsData: Array<{ date: Date; value: number }> = [];

    groupedData.forEach((row) => {
      // Convert seconds to minutes: executionTime is in seconds, convert to minutes
      const executionTimeMinutes = (row.executionTime || 0) / 60;
      const executionTimeSavedMinutes = (row.executionTimeSaved || 0) / 60;

      totalTimeData.push({ date: row.date, value: executionTimeMinutes });
      totalTimeWithoutSavingsData.push({
        date: row.date,
        value: executionTimeMinutes + executionTimeSavedMinutes,
      });
      totalTimeSavingsData.push({
        date: row.date,
        value: executionTimeSavedMinutes,
      });
    });

    return {
      totalTimeData,
      totalTimeWithoutSavingsData,
      totalTimeSavingsData,
    };
  }, [groupedData]);

  const onTooltipHeader = usePartialDataTooltipHeader(groupedData);

  const isEmpty = !isLoading && data.length === 0;

  const emptyStateLegendTilesBuilder = useCallback(() => {
    return [
      createEmptyLegendTile(PRIMARY_DATA_LABEL, 'var(--fgVizIndigo)'),
      createEmptyLegendTile(SECONDARY_DATA_LABEL, 'var(--fgVizTeal)'),
      createEmptyLegendTile(AREA_DATA_LABEL, TEAL_COLOR_WITH_OPACITY),
    ];
  }, []);

  const legendTilesBuilderCallback = useCallback(
    (config: LegendTilesConfig) =>
      legendTilesBuilder({
        config: config,
        value: chartData.totalTimeSavingsData.reduce((sum, row) => sum + row.value, 0),
        label: AREA_DATA_LABEL,
        color: TEAL_COLOR_WITH_OPACITY,
      }),
    [chartData.totalTimeSavingsData],
  );

  return (
    <Section
      header={
        <div className="flex flex-col gap-y-2">
          <div className="flex items-center justify-between gap-x-2">
            <div className="flex items-center gap-x-2">
              <span>Query run time {getTimePeriodLabel(grouping)}</span>
              {infoTooltip}
              {statusBadge}
            </div>
            {selector !== undefined
              ? selector
              : enableGroupingSelector &&
                setGrouping && (
                  <GroupingSelector grouping={grouping} setGrouping={setGrouping} />
                )}
          </div>
          <span className="font-caption">
            Estimated reduction in build time due to state-aware orchestration during
            this period.{' '}
            <Link
              isInternal={false}
              to="https://docs.getdbt.com/docs/explore/cost-insights"
            >
              Learn more.
            </Link>
          </span>
        </div>
      }
    >
      <Chart
        data={chartData.totalTimeData}
        secondaryData={chartData.totalTimeWithoutSavingsData}
        primaryColor="var(--fgVizIndigo)"
        secondaryColor="var(--fgVizTeal)"
        fillSecondaryArea={true}
        secondaryAreaStyle={{
          opacity: 0.1,
          color: 'var(--fgVizTeal)',
        }}
        secondaryValueAccessor={(row: any) => row.value}
        isLoading={isLoading || isPreparing}
        loadingMessage={isPreparing ? 'Preparing cost insights...' : undefined}
        seriesOrder={[PRIMARY_DATA_LABEL, SECONDARY_DATA_LABEL]}
        type="timeSeriesLine"
        yLabel="Build duration"
        primaryDataLabel={PRIMARY_DATA_LABEL}
        secondaryDataLabel={SECONDARY_DATA_LABEL}
        chartType="line"
        fillArea={true}
        timeGrouping={grouping}
        groupAccessor={(row: any) => {
          return row.group;
        }}
        valueAccessor={(row: any) => {
          return row.value;
        }}
        emptyStateMessage="No run data available to view for this period"
        onTooltipHeader={onTooltipHeader}
        formatValue={formatMinutes}
        legendTilesBuilder={
          isEmpty || isLoading || isPreparing
            ? emptyStateLegendTilesBuilder
            : legendTilesBuilderCallback
        }
      />
    </Section>
  );
};
